import React from 'react';

import { ValidationResult } from '../../../common/pokCore/validation/validateAgainst';
import { AttachmentViewTypes } from '../Attachments/AttachmentViewTypes';
import { GetLastEditorDto } from '../../../common/pokCore/autogenerated/pokApiClient';

export interface AddAssociateModalProps<CreateUpdateDto> {
  show: boolean;
  newAssociate: () => CreateUpdateDto;
  validate: (dto: CreateUpdateDto) => Promise<ValidationResult>;
  onCreate: (dto: CreateUpdateDto, cache?: Record<string, string>) => void;
  onClose: () => void;
  companyId: string;
  type?: AttachmentViewTypes;
  dictionaryId?: string;
  isLoading: boolean;
}

export interface AssociateDocumentModalProps<GetDto, CreateUpdateDto> {
  document: GetDto;
  show: boolean;
  readonly?: boolean;
  onGet?: (doc: GetDto, method: string) => Promise<void>;
  onGetLastEditor: (id: string) => Promise<GetLastEditorDto>;
  onApprove?: (id: string) => void;
  onRevertApprove?: (id: string) => void;
  onUpdate?: (id: string, cuDto: CreateUpdateDto) => void;
  validate: (dto: CreateUpdateDto) => Promise<ValidationResult>;
  onDelete: (id: string) => void;
  onClose: () => void;
  type?: AttachmentViewTypes;
  isLoadingSave: boolean;
  isLoadingDelete: boolean;
}

export interface AssociateDocuments<GetDto, CreateUpdateDto> {
  documents: GetDto[];
  labelGenerator: (dto: GetDto) => React.ReactNode;
  new: () => CreateUpdateDto;
  validate: (cuDto: CreateUpdateDto) => Promise<ValidationResult>;
  add: (cuDto: CreateUpdateDto) => Promise<void>;
  update: (id: string, cuDto: CreateUpdateDto) => Promise<void>;
  delete: (id: string) => Promise<void>;
  get?: (dto: GetDto, method: string) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  filter: (
    filterFunc: (dto: GetDto) => boolean,
    createWrapper: (cuDto: CreateUpdateDto) => void,
  ) => AssociateDocuments<GetDto, CreateUpdateDto>;
}

interface AssociateDocumentsWithoutFilter<GetDto, CreateUpdateDto> {
  documents: GetDto[];
  labelGenerator: (dto: GetDto) => React.ReactNode;
  new: () => CreateUpdateDto;
  validate: (cuDto: CreateUpdateDto) => Promise<ValidationResult>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  add: (cuDto: CreateUpdateDto) => Promise<void>;
  update: (id: string, cuDto: CreateUpdateDto) => Promise<void>;
  delete: (id: string) => Promise<void>;
  get?: (dto: GetDto, method: string) => Promise<void>;
}

export const AssociateDocumentsWithFilter = <GetDto, CreateUpdateDto>(
  associate: AssociateDocumentsWithoutFilter<GetDto, CreateUpdateDto>,
) => {
  const filterMethod = (
    filterFunc: (dto: GetDto) => boolean,
    createWrapper: (dto: CreateUpdateDto) => void,
  ) => {
    return {
      ...associate,
      documents: associate.documents.filter(filterFunc),
      new: () => {
        const dto = associate.new();
        createWrapper(dto);
        return dto;
      },
      add: async (dto: CreateUpdateDto) => {
        createWrapper(dto);
        await associate.add(dto);
      },
      filter: filterMethod,
    };
  };

  return {
    ...associate,
    filter: filterMethod,
  };
};
